import React, { useState } from 'react';
import { REAL_QUESTION_BANK, shuffle } from '../data/questionBank';

export default function PrintableExamGenerator() {
  const [selectedGrade, setSelectedGrade] = useState('6');
  const [selectedUnit, setSelectedUnit] = useState('1');
  const [questionCount, setQuestionCount] = useState(15);
  const [examVersion, setExamVersion] = useState('A');
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);
  const [includeBubbleSheet, setIncludeBubbleSheet] = useState(true);
  const [randomize, setRandomize] = useState(true);
  const [generatedQuestions, setGeneratedQuestions] = useState(() => {
    const raw = REAL_QUESTION_BANK['6']?.[1] || [];
    return raw.slice(0, 15);
  });

  // Nombres de unidades pedagógicas
  const unitTitles = {
    '6': {
      1: 'Greetings, Alphabet, Personal Info & Numbers',
      2: 'Family Members, Home & Possessives',
      3: 'Daily Routines & Present Simple',
      4: 'School Subjects, Likes & Preferences',
      5: "Abilities (Can / Can't) & Hobbies"
    },
    '7': {
      1: 'Feelings, Emotions & Health Issues',
      2: 'Free Time Activities & Frequency Adverbs',
      3: 'City Places, Directions & Prepositions',
      4: 'Food, Nutrition & Countable / Uncountable Nouns',
      5: 'Past Simple (Was / Were) & Biographies'
    },
    '9': {
      1: 'Environmental Awareness & Passive Voice',
      2: "Inventions, Technology & 'Used to'",
      3: 'Travel, World Cultures & Present Perfect',
      4: 'Future Plans (Will vs. Going to) & Conditionals',
      5: 'Modal Verbs & Complex ICFES Reading Comprehension'
    }
  };

  // Generar conjunto de preguntas
  const handleGenerateExam = () => {
    let pool = REAL_QUESTION_BANK[selectedGrade]?.[selectedUnit] || [];
    if (pool.length === 0) {
      alert('No hay preguntas disponibles para esta combinación.');
      return;
    }

    let processed = randomize ? shuffle(pool) : [...pool];
    let selected = processed.slice(0, Math.min(questionCount, processed.length));
    
    setGeneratedQuestions(selected);
  };

  // Disparar diálogo nativo de impresión / PDF
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      
      {/* PANEL DE CONFIGURACIÓN (OCULTO AL IMPRIMIR) */}
      <div className="print:hidden bg-slate-900/90 border border-slate-800 p-6 md:p-8 rounded-3xl shadow-2xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4">
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-sky-400 bg-sky-950/80 px-3 py-1 rounded-full border border-sky-500/30">
              📄 Plan de Contingencia Offline
            </span>
            <h2 className="text-xl md:text-2xl font-black text-white mt-2 flex items-center gap-2">
              <span>🖨️</span> Generador de Pruebas Físicas e Imprimibles (PDF)
            </h2>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Genera exámenes listos para fotocopiar con formato oficial de la IE Enrique Olaya Herrera, hoja de respuestas tipo ICFES/Saber y clave de calificación para el docente.
            </p>
          </div>

          <button
            onClick={handlePrint}
            className="btn-3d btn-3d-emerald px-6 py-3 text-xs md:text-sm font-black gap-2 shadow-2xl"
          >
            🖨️ Imprimir / Guardar en PDF 🚀
          </button>
        </div>

        {/* SELECTORES DE CONFIGURACIÓN */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* GRADO */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300">Grado Académico:</label>
            <select
              value={selectedGrade}
              onChange={(e) => {
                setSelectedGrade(e.target.value);
                const pool = REAL_QUESTION_BANK[e.target.value]?.[selectedUnit] || [];
                setGeneratedQuestions((randomize ? shuffle(pool) : pool).slice(0, questionCount));
              }}
              className="w-full bg-slate-950 border border-slate-700 text-white text-xs font-bold p-3 rounded-xl outline-none focus:border-sky-500 cursor-pointer"
            >
              <option value="6">Grado 6° (Nivel A1)</option>
              <option value="7">Grado 7° (Nivel A1-A2)</option>
              <option value="9">Grado 9° (Nivel A2-B1)</option>
            </select>
          </div>

          {/* UNIDAD */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300">Unidad Temática:</label>
            <select
              value={selectedUnit}
              onChange={(e) => {
                setSelectedUnit(e.target.value);
                const pool = REAL_QUESTION_BANK[selectedGrade]?.[e.target.value] || [];
                setGeneratedQuestions((randomize ? shuffle(pool) : pool).slice(0, questionCount));
              }}
              className="w-full bg-slate-950 border border-slate-700 text-white text-xs font-bold p-3 rounded-xl outline-none focus:border-sky-500 cursor-pointer"
            >
              <option value="1">Unidad 1 - {unitTitles[selectedGrade]?.[1]}</option>
              <option value="2">Unidad 2 - {unitTitles[selectedGrade]?.[2]}</option>
              <option value="3">Unidad 3 - {unitTitles[selectedGrade]?.[3]}</option>
              <option value="4">Unidad 4 - {unitTitles[selectedGrade]?.[4]}</option>
              <option value="5">Unidad 5 - {unitTitles[selectedGrade]?.[5]}</option>
            </select>
          </div>

          {/* NÚMERO DE PREGUNTAS */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300">Preguntas a Evaluar:</label>
            <select
              value={questionCount}
              onChange={(e) => {
                const count = Number(e.target.value);
                setQuestionCount(count);
                const pool = REAL_QUESTION_BANK[selectedGrade]?.[selectedUnit] || [];
                setGeneratedQuestions((randomize ? shuffle(pool) : pool).slice(0, count));
              }}
              className="w-full bg-slate-950 border border-slate-700 text-white text-xs font-bold p-3 rounded-xl outline-none focus:border-sky-500 cursor-pointer"
            >
              <option value={10}>10 Preguntas (Prueba Rápida)</option>
              <option value={15}>15 Preguntas (Estándar ICFES)</option>
              <option value={20}>20 Preguntas (Evaluación Bimestral)</option>
              <option value={25}>25 Preguntas (Banco Completo)</option>
            </select>
          </div>

          {/* VERSIÓN DE EXAMEN */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300">Versión de Examen:</label>
            <select
              value={examVersion}
              onChange={(e) => setExamVersion(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 text-white text-xs font-bold p-3 rounded-xl outline-none focus:border-sky-500 cursor-pointer"
            >
              <option value="A">Versión A</option>
              <option value="B">Versión B (Barajada)</option>
              <option value="C">Versión C (Barajada)</option>
            </select>
          </div>

        </div>

        {/* CASILLAS DE VERIFICACIÓN */}
        <div className="flex flex-wrap gap-6 pt-2 text-xs font-bold text-slate-300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={includeBubbleSheet} 
              onChange={(e) => setIncludeBubbleSheet(e.target.checked)}
              className="w-4 h-4 rounded text-blue-600 focus:ring-0 cursor-pointer"
            />
            Incluir Hoja de Respuestas Tipo Saber (Burbujas)
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={includeAnswerKey} 
              onChange={(e) => setIncludeAnswerKey(e.target.checked)}
              className="w-4 h-4 rounded text-emerald-600 focus:ring-0 cursor-pointer"
            />
            Incluir Clave de Respuestas y Rúbrica para el Docente
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={randomize} 
              onChange={(e) => setRandomize(e.target.checked)}
              className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
            />
            Barajar aleatoriamente el orden de preguntas
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={handleGenerateExam}
            className="btn-3d btn-3d-blue px-6 py-2.5 text-xs font-bold gap-2"
          >
            🔄 Regenerar / Mezclar Preguntas
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VISTA DEL DOCUMENTO IMPRIMIBLE (ESTILO BLANCO Y NEGRO PARA FOTOCOPIA) */}
      {/* ========================================================================= */}
      <div id="printable-exam-sheet" className="printable-exam-sheet bg-white text-black p-6 md:p-10 rounded-2xl shadow-2xl border border-slate-300 print:border-none print:shadow-none print:p-0 print:m-0 print:rounded-none font-sans space-y-6">
        
        {/* 1. ENCABEZADO INSTITUCIONAL OFICIAL */}
        <header className="border-b-2 border-black pb-4 space-y-3">
          <div className="flex items-center justify-between border-b border-black pb-2">
            <div>
              <h1 className="text-base md:text-lg font-black uppercase tracking-tight">
                Institución Educativa Enrique Olaya Herrera
              </h1>
              <p className="text-xs font-bold text-gray-700">
                Área de Humanidades: Lengua Extranjera (Inglés) • Medellín, Antioquia
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-black border-2 border-black px-2.5 py-1 rounded">
                VERSIÓN {examVersion}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center text-xs flex-wrap gap-2">
            <div>
              <strong>Docente:</strong> Lic. Didier Antonio Alvarez Ramos
            </div>
            <div>
              <strong>Prueba:</strong> Saber / ICFES — Grado {selectedGrade}° (Unidad {selectedUnit})
            </div>
            <div>
              <strong>Duración:</strong> 45 minutos
            </div>
          </div>

          <div className="text-xs italic bg-gray-100 p-2 border border-gray-300 rounded print:bg-white print:border-black">
            <strong>Tema Curricular:</strong> {unitTitles[selectedGrade]?.[selectedUnit]}
          </div>

          {/* DATOS DEL ESTUDIANTE */}
          <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4 pt-2 text-xs font-bold">
            <div className="border-b border-black pb-1">
              Nombre: <span className="font-normal text-gray-400">__________________________________________</span>
            </div>
            <div className="border-b border-black pb-1">
              Grupo: <span className="font-normal text-gray-400">________</span>
            </div>
            <div className="border-b border-black pb-1">
              Fecha: <span className="font-normal text-gray-400">____ / ____ / 2026</span>
            </div>
          </div>
        </header>

        {/* INSTRUCCIONES GENERALES */}
        <div className="text-xs leading-relaxed border-l-4 border-black pl-3 py-1 bg-gray-50 print:bg-white">
          <strong>INSTRUCCIONES:</strong> Lea con atención cada enunciado y seleccione la opción correcta. Si dispone de la hoja de respuestas tipo Saber, rellene completamente el círculo correspondiente con lápiz o bolígrafo negro sin salir del contorno.
        </div>

        {/* 2. BANCO DE PREGUNTAS EN FORMATO EXAMEN */}
        <section className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-x-8 gap-y-6">
            {generatedQuestions.map((item, idx) => (
              <div key={idx} className="space-y-2 border-b border-gray-200 print:border-gray-400 pb-3 break-inside-avoid">
                <div className="flex items-baseline gap-2">
                  <span className="font-black text-xs bg-black text-white px-2 py-0.5 rounded print:border print:border-black">
                    {idx + 1}
                  </span>
                  <span className="text-[10px] font-bold uppercase text-gray-500 print:text-black">
                    [{item.type || 'ICFES'}]
                  </span>
                </div>
                
                <p className="text-xs font-bold whitespace-pre-line text-gray-900 leading-snug">
                  {item.q}
                </p>

                <div className="space-y-1 pl-2 text-xs text-gray-800">
                  {item.opts.map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-start gap-2">
                      <span className="font-semibold">{opt}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ========================================================================= */}
        {/* 3. HOJA DE RESPUESTAS TIPO SABER / ICFES */}
        {/* ========================================================================= */}
        {includeBubbleSheet && (
          <div className="page-break-before pt-6 border-t-2 border-black space-y-4 print:mt-8">
            <div className="flex justify-between items-center border-b border-black pb-2">
              <div>
                <h3 className="text-sm font-black uppercase">
                  Hoja de Respuestas — Prueba Saber / ICFES
                </h3>
                <p className="text-[10px] text-gray-600">
                  IE Enrique Olaya Herrera • Grado {selectedGrade}° — Unidad {selectedUnit} (Versión {examVersion})
                </p>
              </div>
              <div className="text-right text-xs font-bold">
                Valoración: ________ / 5.0
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 print:grid-cols-5 gap-3 text-xs">
              {generatedQuestions.map((_, idx) => (
                <div key={idx} className="border border-gray-300 p-2 rounded flex items-center justify-between print:border-black">
                  <span className="font-black w-6">{idx + 1}.</span>
                  <div className="flex gap-1.5">
                    {['A', 'B', 'C', 'D'].map((letter) => (
                      <span
                        key={letter}
                        className="w-5 h-5 rounded-full border border-black flex items-center justify-center font-bold text-[10px]"
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center text-[10px] text-gray-500 pt-2">
              <span>Firma del Estudiante: _______________________________</span>
              <span>Firma del Docente: Lic. Didier Alvarez</span>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 4. CLAVE DE RESPUESTAS Y RÚBRICA PEDAGÓGICA (SOLO PARA EL DOCENTE) */}
        {/* ========================================================================= */}
        {includeAnswerKey && (
          <div className="page-break-before pt-6 border-t-2 border-dashed border-black space-y-4 print:mt-8">
            <div className="bg-gray-100 p-3 rounded border border-gray-300 print:bg-white print:border-black">
              <h3 className="text-xs font-black uppercase text-black flex items-center justify-between">
                <span>🔑 CLAVE DE RESPUESTAS Y JUSTIFICACIÓN PEDAGÓGICA (USO EXCLUSIVO DOCENTE)</span>
                <span className="text-[10px] font-normal">Grado {selectedGrade}° • Unidad {selectedUnit} • Versión {examVersion}</span>
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {generatedQuestions.map((item, idx) => (
                <div key={idx} className="border border-gray-200 p-2.5 rounded space-y-1 bg-gray-50 print:bg-white print:border-black text-[11px]">
                  <div className="flex justify-between items-center">
                    <span className="font-black">Pregunta {idx + 1}:</span>
                    <span className="font-black bg-black text-white px-2 py-0.5 rounded print:border print:border-black">
                      {item.ans}
                    </span>
                  </div>
                  <p className="text-gray-700 italic">
                    <strong>Justificación:</strong> {item.exp || 'Respuesta canónica de alineación curricular.'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

    </div>
  );
}